import { apiClient } from './api/apiClient';
import { API_ENDPOINTS } from '../config';
import { AuthResponse, User, ApiResponse } from '../types';

/**
 * Authentication Service
 * Handles login, registration, token management
 */
export class AuthService {
  private static readonly ACCESS_TOKEN_KEY = 'accessToken';
  private static readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private static readonly USER_KEY = 'user';

  /**
   * Register new user/admin
   */
  static async register(data: {
    name: string;
    email: string;
    password: string;
    role: 'user' | 'admin' | 'superadmin' | 'manager';
  }): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<AuthResponse>(
      API_ENDPOINTS.AUTH_REGISTER,
      data,
      false
    );

    if (response.statusCode === 200 && response.data) {
      this.saveTokens(response.data.accessToken, response.data.refreshToken);
      this.saveUser(response.data.user);
    }

    return response;
  }

  /**
   * Login user
   */
  static async login(data: {
    email: string;
    password: string;
  }): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<AuthResponse>(
      API_ENDPOINTS.AUTH_LOGIN,
      data,
      false
    );

    if (response.statusCode === 200 && response.data) {
      this.saveTokens(response.data.accessToken, response.data.refreshToken);
      this.saveUser(response.data.user);
    }

    return response;
  }

  /**
   * Get current logged-in user
   */
  static async getCurrentUser(): Promise<ApiResponse<User>> {
    return apiClient.get<User>(API_ENDPOINTS.AUTH_ME, true);
  }

  /**
   * Refresh access token
   */
  static async refreshToken(): Promise<ApiResponse<AuthResponse>> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      return {
        statusCode: 401,
        data: null,
        error: 'No refresh token available',
      };
    }

    const response = await apiClient.post<AuthResponse>(
      API_ENDPOINTS.AUTH_REFRESH,
      { refreshToken },
      false
    );

    if (response.statusCode === 200 && response.data) {
      this.saveTokens(response.data.accessToken, response.data.refreshToken);
    }

    return response;
  }

  /**
   * Logout user (current device only)
   */
  static async logout(): Promise<ApiResponse<{ success: boolean }>> {
    const response = await apiClient.post<{ success: boolean }>(
      API_ENDPOINTS.AUTH_LOGOUT,
      {},
      true
    );

    if (response.statusCode === 200) {
      this.clearTokens();
      this.clearUser();
    }

    return response;
  }

  /**
   * Logout from all devices
   */
  static async logoutAll(): Promise<ApiResponse<{ success: boolean }>> {
    const response = await apiClient.post<{ success: boolean }>(
      `${API_ENDPOINTS.AUTH_LOGOUT}-all`,
      {},
      true
    );

    if (response.statusCode === 200) {
      this.clearTokens();
      this.clearUser();
    }

    return response;
  }

  /**
   * Get current user profile
   */
  static async getProfile(): Promise<ApiResponse<User>> {
    return apiClient.get<User>(API_ENDPOINTS.AUTH_ME, true);
  }

  /**
   * Reset password for current user
   */
  static async resetPassword(data: {
    newPassword: string;
  }): Promise<ApiResponse<{ message: string }>> {
    return apiClient.post<{ message: string }>(
      API_ENDPOINTS.AUTH_RESET_PASSWORD,
      data,
      true
    );
  }

  /**
   * Check if user is authenticated and token not expired
   */
  static isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }

    // Check if token is expired
    if (this.isTokenExpired(token)) {
      this.logout();
      return false;
    }

    return true;
  }

  /**
   * Check if JWT token is expired
   */
  private static isTokenExpired(token: string): boolean {
    try {
      // JWT format: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        return true;
      }

      // Decode payload (base64 decode)
      const payload = JSON.parse(
        atob(parts[1])
      );

      // Check if exp claim exists and compare with current time
      if (payload.exp) {
        const expirationTime = payload.exp * 1000; // Convert to milliseconds
        return Date.now() >= expirationTime;
      }

      return false;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  /**
   * Get access token
   */
  static getAccessToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(this.ACCESS_TOKEN_KEY);
    }
    return null;
  }

  /**
   * Get refresh token
   */
  static getRefreshToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    }
    return null;
  }

  /**
   * Get stored user
   */
  static getUser(): User | null {
    if (typeof window !== 'undefined') {
      const user = localStorage.getItem(this.USER_KEY);
      return user ? JSON.parse(user) : null;
    }
    return null;
  }

  /**
   * Check if user is admin
   */
  static isAdmin(): boolean {
    const user = this.getUser();
    return user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'manager';
  }

  /**
   * Check if user is superadmin
   */
  static isSuperAdmin(): boolean {
    const user = this.getUser();
    return user?.role === 'superadmin';
  }

  /**
   * Save tokens to localStorage
   */
  private static saveTokens(accessToken: string, refreshToken: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * Save user to localStorage
   */
  private static saveUser(user: User): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    }
  }

  /**
   * Clear tokens from localStorage
   */
  static clearTokens(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(this.ACCESS_TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    }
  }

  /**
   * Clear user from localStorage
   */
  private static clearUser(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(this.USER_KEY);
    }
  }
}

export default AuthService;
